import { useState, useEffect } from 'react';
import { Info, Eye } from 'lucide-react';
import { useConfigTab, FORM_STYLES, Toggle, SaveButton, ConfigTimestamps, LoadingState } from './shared';

const PREDEFINED_STATUSES = [
  'ORDER_PLACED', 'ORDER_CONFIRMED', 'PICKED_UP', 'IN_TRANSIT',
  'OUT_FOR_DELIVERY', 'DELIVERED', 'RTO_INITIATED', 'RTO_DELIVERED',
  'CANCELLED', 'RETURN_INITIATED', 'RETURN_PICKED_UP', 'RETURN_DELIVERED',
];

const SAMPLE_IMAGE = '/images/samples/whatsapp-order-status-notification.png';

function parseStatusEntries(val) {
  if (!val) return [];
  const map =
    val.whats_app_status_template_mapping ||
    val.eligible_order_status_to_order_status_text_for_whats_app_notification;
  if (!map || typeof map !== 'object') return [];
  return Object.entries(map).map(([status, template]) => ({ status, template }));
}

export function WhatsappNotificationTab({ merchantId, reloadKey }) {
  const { loading, saving, isNew, configValue, metadata, saveConfig } = useConfigTab(
    merchantId, 'MERCHANT_WHATSAPP_NOTIFICATION_CONFIGURATIONS', reloadKey,
  );

  const [enabled, setEnabled] = useState(false);
  const [senderName, setSenderName] = useState('');
  const [statusTemplateEntries, setStatusTemplateEntries] = useState([]);
  const [testingEnabled, setTestingEnabled] = useState(false);
  const [testingReceiver, setTestingReceiver] = useState('');
  const [chargeAmount, setChargeAmount] = useState('');
  const [chargeCurrency, setChargeCurrency] = useState('');

  useEffect(() => {
    if (!configValue) {
      setEnabled(false);
      setSenderName('');
      setStatusTemplateEntries([]);
      setTestingEnabled(false);
      setTestingReceiver('');
      setChargeAmount('');
      setChargeCurrency('');
      return;
    }
    setEnabled(
      configValue.is_whats_app_notification_enabled === true ||
      configValue.whats_app_notification_enabled === true
    );
    setSenderName(
      configValue.whats_app_notification_sender_name ??
      configValue.whats_app_business_number ?? ''
    );
    setStatusTemplateEntries(parseStatusEntries(configValue));
    setTestingEnabled(
      configValue.is_whats_app_testing_mode_enabled === true ||
      configValue.testing_mode_enabled === true
    );
    setTestingReceiver(
      configValue.whats_app_testing_receiver_number ??
      configValue.testing_mode_notification_receiver ?? ''
    );
    const charge = configValue.notification_charge;
    const rawAmount = configValue.whats_app_charge_amount ?? (charge && charge.amount);
    const rawCurrency = configValue.whats_app_charge_currency ?? (charge && charge.currency);
    setChargeAmount(rawAmount != null ? String(rawAmount) : '');
    setChargeCurrency(rawCurrency ?? '');
  }, [configValue]);

  const updateStatusTemplate = (index, field, value) => {
    setStatusTemplateEntries((prev) => prev.map((e, i) => (i === index ? { ...e, [field]: value } : e)));
  };

  const addStatusEntry = () => {
    const used = new Set(statusTemplateEntries.map((e) => e.status));
    const next = PREDEFINED_STATUSES.find((s) => !used.has(s)) || '';
    setStatusTemplateEntries((prev) => [...prev, { status: next, template: '' }]);
  };

  const removeStatusEntry = (index) => {
    setStatusTemplateEntries((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSave = () => {
    const statusMap = {};
    for (const entry of statusTemplateEntries) {
      if (entry.status && entry.template.trim()) {
        statusMap[entry.status] = entry.template.trim();
      }
    }

    const payload = {
      whats_app_notification_enabled: enabled,
      whats_app_notification_sender_name: senderName.trim() || null,
      eligible_order_status_to_order_status_text_for_whats_app_notification: statusMap,
      testing_mode_enabled: testingEnabled,
      testing_mode_notification_receiver: testingReceiver.trim(),
    };

    if (chargeAmount !== '') {
      payload.notification_charge = {
        amount: parseFloat(chargeAmount),
        currency: chargeCurrency || 'INR',
      };
    }

    saveConfig({ merchant_whats_app_notification_configurations_value: payload });
  };

  if (loading) return <LoadingState />;

  return (
    <div className="flex flex-col xl:flex-row gap-4 xl:gap-5">
      <div className="flex-1 min-w-0 space-y-4">
        {isNew && (
          <div className="flex items-center gap-2 px-3 py-2.5 bg-blue-50 border border-blue-100 rounded-lg text-xs sm:text-sm text-blue-700">
            <Info className="w-4 h-4 shrink-0" />
            No existing configuration - fill the form below and save to create one.
          </div>
        )}

        <div className={FORM_STYLES.sectionCard}>
          <h3 className={FORM_STYLES.sectionTitle}>General</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-start">
            <Toggle
              id="wa-enabled"
              checked={enabled}
              onChange={setEnabled}
              label="WhatsApp Notifications Enabled"
              description="Enable WhatsApp for shipment status updates"
            />
            <div>
              <label className={FORM_STYLES.label}>Sender Name</label>
              <input
                type="text"
                value={senderName}
                onChange={(e) => setSenderName(e.target.value)}
                placeholder="e.g. My Store"
                className={FORM_STYLES.input}
              />
            </div>
          </div>
        </div>

        <div className={FORM_STYLES.sectionCard}>
          <h3 className={FORM_STYLES.sectionTitle}>Status Template Mapping</h3>
          <p className="text-xs text-slate-500 mb-3">Configure the WhatsApp template sent for each shipment status update.</p>

          {statusTemplateEntries.length === 0 && (
            <p className="text-sm text-slate-500 italic mb-3">No status mappings configured.</p>
          )}

          <div className="space-y-3">
            {statusTemplateEntries.map((entry, idx) => (
              <div key={idx} className="flex items-start gap-2">
                <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <select
                    value={entry.status}
                    onChange={(e) => updateStatusTemplate(idx, 'status', e.target.value)}
                    className={FORM_STYLES.input}
                  >
                    <option value="">Select status…</option>
                    {PREDEFINED_STATUSES.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                  <input
                    type="text"
                    value={entry.template}
                    onChange={(e) => updateStatusTemplate(idx, 'template', e.target.value)}
                    placeholder="Template name for this status"
                    className={FORM_STYLES.input}
                  />
                </div>
                <button type="button" onClick={() => removeStatusEntry(idx)} className={FORM_STYLES.btnDanger}>
                  &times;
                </button>
              </div>
            ))}
          </div>

          <button type="button" onClick={addStatusEntry} className={`${FORM_STYLES.btnSecondary} mt-3`}>
            + Add Status Mapping
          </button>
        </div>

        <div className={FORM_STYLES.sectionCard}>
          <h3 className={FORM_STYLES.sectionTitle}>Charge Configuration</h3>
          <p className="text-[11px] text-slate-400 mb-3">*Inclusive of GST. Contact support to help.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={FORM_STYLES.label}>Charge Amount (₹)</label>
              <input type="text" value={chargeAmount} readOnly disabled className={FORM_STYLES.inputDisabled} />
            </div>
            <div>
              <label className={FORM_STYLES.label}>Currency</label>
              <input type="text" value={chargeCurrency} readOnly disabled className={FORM_STYLES.inputDisabled} />
            </div>
          </div>
        </div>

        <SaveButton saving={saving} onClick={handleSave} />
        {!isNew && <ConfigTimestamps metadata={metadata} />}
      </div>

      <div className="xl:w-[260px] 2xl:w-[300px] shrink-0 order-first xl:order-last">
        <div className="xl:sticky xl:top-4 rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
          <div className="px-3 sm:px-4 py-2.5 sm:py-3 bg-slate-50 border-b border-slate-100">
            <div className="flex items-center gap-2">
              <Eye className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-slate-500" />
              <span className="text-[10px] sm:text-xs font-semibold text-slate-600 uppercase tracking-wider">Sample Preview</span>
            </div>
            <p className="text-[10px] sm:text-[11px] text-slate-400 mt-0.5">How notifications appear to customers</p>
          </div>
          <div className="p-2 sm:p-3">
            <img
              src={SAMPLE_IMAGE}
              alt="WhatsApp order status notification sample"
              className="w-full h-auto rounded-lg border border-slate-100 shadow-sm"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
